import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Send, Check, Plus, X, Bell, Copy, Download, Printer, Clock } from '../icons';
import { useAuth } from '../context/AuthContext';
import { useConfirm, usePrompt } from '../components/Modal';
import {
  getContract, updateContract, sendContract, remindContract, duplicateContract,
  countersignContract, cancelContract, downloadContractDocument,
  getContractTemplates, createContractTemplate, updateContractTemplate,
} from '../services/api';
import { CONTRACT_STATUS, errMsg } from './StudioContracts';

// Filled automatically from the contract's own details — not editable as fields
const VARIABLES = [
  ['client_name', 'Primary client name'],
  ['client_names', 'All signer names'],
  ['client_email', 'Client email'],
  ['project_name', 'Project name'],
  ['event_date', 'Event date'],
  ['studio_name', 'Studio name'],
  ['today', "Today's date"],
];
const BUILTIN_KEYS = VARIABLES.map(([k]) => k);
const FIELD_KEY_RE = /^[a-z][a-z0-9_]{0,39}$/;

const guessFieldType = (key) => {
  if (/(^|_)(date|deadline|due|day)(_|$)/.test(key)) return 'date';
  if (/(fee|amount|price|cost|rate|deposit|balance|total|payment)/.test(key)) return 'money';
  return 'text';
};
// "balance_due_date" -> "Balance due date"
const fieldLabel = (key) => key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

// Mirrors the server's fallbacks so the editor shows what the client will get
const DEFAULT_STUDIO = 'NepWed Studio';
const studioOf = (form) => (form.studio_name || '').trim() || DEFAULT_STUDIO;
const defaultSubject = (form) => `${studioOf(form)} sent you a contract to sign: ${form.name || 'Contract'}`;

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const fmtLong = (d) => {
  if (!d) return '';
  const date = typeof d === 'string' && d.length === 10 ? new Date(`${d}T00:00:00`) : new Date(d);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};
const fmtStamp = (d) => (d ? new Date(d).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '');
const initial = (s) => (s || '?').trim()[0]?.toUpperCase() || '?';
const defaultExpiry = () => { const d = new Date(); d.setDate(d.getDate() + 14); return d.toISOString().slice(0, 10); };

function ContractEditor({ mode = 'contract' }) {
  const isTemplate = mode === 'template';
  const { id } = useParams();
  const isNewTemplate = isTemplate && id === 'new';
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [confirmDialog, ConfirmDialog] = useConfirm();
  const [promptDialog, PromptDialog] = usePrompt();
  const editorRef = useRef(null);
  const [loading, setLoading] = useState(!isNewTemplate);
  const [loadError, setLoadError] = useState('');
  const [contract, setContract] = useState(null);
  const [form, setForm] = useState({
    name: '', description: '', project_name: '', event_date: '',
    expires_at: '', studio_name: '', email_subject: '',
    reminders_enabled: false, reminder_interval_days: 3,
  });
  const [signers, setSigners] = useState([{ name: '', email: '' }]);
  const [fields, setFields] = useState({});
  const [html, setHtml] = useState('');
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState('');
  // Caret position inside the editor, so clicking a field in the side panel
  // still inserts where the studio was typing.
  const savedRange = useRef(null);

  const readOnly = !isTemplate && contract && contract.status !== 'draft';
  const backPath = '/admin/studio/contracts';

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') navigate('/login');
  }, [user, authLoading, navigate]);

  const load = useCallback(async () => {
    if (isNewTemplate) return;
    setLoading(true);
    setLoadError('');
    try {
      if (isTemplate) {
        const res = await getContractTemplates();
        const t = res.data.find((x) => String(x.id) === String(id));
        if (!t) throw new Error('Template not found');
        setForm((f) => ({ ...f, name: t.name, description: t.description || '' }));
        setHtml(t.content);
      } else {
        const res = await getContract(id);
        const c = res.data;
        setContract(c);
        setForm({
          name: c.name, description: '', project_name: c.project_name || '', event_date: c.event_date || '',
          expires_at: c.expires_at || '', studio_name: c.studio_name || '', email_subject: c.email_subject || '',
          reminders_enabled: !!c.reminders_enabled,
          reminder_interval_days: c.reminder_interval_days || 3,
        });
        setSigners(c.signers?.length
          ? c.signers.map((s) => ({ name: s.name, email: s.email, phone: s.phone || '' }))
          : [{ name: c.client_name, email: c.client_email, phone: '' }]);
        setFields(c.custom_fields || {});
        setHtml(c.content);
      }
    } catch (err) {
      setLoadError(errMsg(err, err.message || 'Could not load'));
    } finally {
      setLoading(false);
    }
  }, [id, isTemplate, isNewTemplate]);

  useEffect(() => {
    if (user?.role === 'admin') load();
  }, [user, load]);

  // Uncontrolled editor so the caret never jumps; push HTML in only on (re)load
  useEffect(() => {
    if (editorRef.current && !readOnly && !preview) editorRef.current.innerHTML = html;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, readOnly, preview, contract?.id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => () => setForm((f) => ({ ...f, [k]: !f[k] }));
  const currentHtml = () => (editorRef.current && !readOnly && !preview ? editorRef.current.innerHTML : html);

  const setSigner = (i, k) => (e) => setSigners((list) => list.map((s, idx) => (idx === i ? { ...s, [k]: e.target.value } : s)));
  const addSigner = () => setSigners((list) => (list.length >= 5 ? list : [...list, { name: '', email: '', phone: '' }]));
  const removeSigner = (i) => setSigners((list) => (list.length <= 1 ? list : list.filter((_, idx) => idx !== i)));

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && editorRef.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  // Insert {{key}} at the last caret position (or the end if there was none)
  const insertField = (key) => {
    const el = editorRef.current;
    if (!el || readOnly || preview) return;
    el.focus();
    const sel = window.getSelection();
    if (savedRange.current) { sel.removeAllRanges(); sel.addRange(savedRange.current); }
    document.execCommand('insertText', false, `{{${key}}}`);
    saveSelection();
    setFields((f) => (f[key] || BUILTIN_KEYS.includes(key) ? f : { ...f, [key]: { value: '', type: guessFieldType(key) } }));
  };

  const setFieldValue = (key, value) => setFields((f) => ({ ...f, [key]: { ...f[key], value } }));
  const setFieldType = (key, type) => setFields((f) => ({ ...f, [key]: { ...f[key], type } }));
  const removeField = (key) => setFields((f) => { const n = { ...f }; delete n[key]; return n; });

  const addField = async () => {
    const raw = await promptDialog({
      title: 'Add a field',
      message: 'Name it the way you would write it, for example "venue" or "balance due date".',
      placeholder: 'venue',
    });
    if (!raw) return;
    const key = raw.trim().toLowerCase().replace(/[\s-]+/g, '_').replace(/[^a-z0-9_]/g, '');
    if (!FIELD_KEY_RE.test(key)) { toast.error('Use letters, numbers and spaces, starting with a letter'); return; }
    if (BUILTIN_KEYS.includes(key)) { toast.error(`"${key}" is already filled in automatically`); return; }
    if (fields[key]) { toast.info('That field already exists'); return; }
    setFields((f) => ({ ...f, [key]: { value: '', type: guessFieldType(key) } }));
    insertField(key);
  };

  const emptyFields = Object.entries(fields).filter(([, f]) => !String(f?.value || '').trim()).map(([k]) => k);

  // Resolves {{variables}} the same way the server will, for the subject preview
  const resolveText = (text) => {
    const vars = {
      client_name: signers[0]?.name || '', client_names: signers.map((s) => s.name).filter(Boolean).join(' and '),
      client_email: signers[0]?.email || '', project_name: form.project_name,
      event_date: fmtLong(form.event_date), studio_name: studioOf(form), today: fmtLong(new Date()),
    };
    for (const [k, f] of Object.entries(fields)) {
      if (!(k in vars)) vars[k] = f.type === 'date' ? fmtLong(f.value) : (f.value || '');
    }
    return String(text || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => (k in vars ? String(vars[k] ?? '') : m));
  };

  const exec = (cmd, value) => { editorRef.current?.focus(); document.execCommand(cmd, false, value); };
  const toolbarBtn = (label, title, onClick, extra = '') => (
    <button type="button" className={`sc-tb-btn ${extra}`} title={title} onMouseDown={(e) => { e.preventDefault(); onClick(); }}>{label}</button>
  );

  const save = async () => {
    const content = currentHtml();
    if (isTemplate) {
      if (!form.name.trim()) { toast.error('Template name is required'); return null; }
      const payload = { name: form.name, description: form.description, content };
      const res = isNewTemplate ? await createContractTemplate(payload) : await updateContractTemplate(id, payload);
      return res.data;
    }
    const res = await updateContract(id, {
      name: form.name, project_name: form.project_name, event_date: form.event_date || null,
      expires_at: form.expires_at || null, studio_name: form.studio_name, email_subject: form.email_subject,
      reminders_enabled: form.reminders_enabled,
      reminder_interval_days: form.reminder_interval_days, signers, custom_fields: fields, content,
    });
    return res.data;
  };

  const applyContract = (c) => {
    setContract(c);
    setHtml(c.content);
    if (c.signers) setSigners(c.signers.map((s) => ({ name: s.name, email: s.email, phone: s.phone || '' })));
    if (c.custom_fields) setFields(c.custom_fields);
  };

  const handleSave = async () => {
    setBusy('save');
    try {
      const saved = await save();
      if (!saved) return;
      if (isNewTemplate) { toast.success('Template created'); navigate(`/admin/studio/templates/${saved.id}`, { replace: true }); return; }
      if (isTemplate) setHtml(saved.content); else applyContract(saved);
      toast.success('Saved');
    } catch (err) {
      toast.error(errMsg(err, 'Could not save'));
    } finally { setBusy(''); }
  };

  const handleSend = async () => {
    const resend = contract.status === 'awaiting_signature';
    const names = signers.map((s) => s.name).filter(Boolean).join(', ');
    const warn = !resend && emptyFields.length
      ? ` ${emptyFields.length} field${emptyFields.length === 1 ? '' : 's'} (${emptyFields.map(fieldLabel).join(', ')}) ${emptyFields.length === 1 ? 'is' : 'are'} still empty and will appear blank in the contract.`
      : '';
    const ok = await confirmDialog({
      title: resend ? 'Resend contract' : 'Send contract',
      message: `${resend ? 'Resend the signing link' : 'Email the signing link'} to ${names}? Each signer gets their own link to sign.${warn}`,
      danger: !!warn,
      confirmText: 'Send',
    });
    if (!ok) return;
    setBusy('send');
    try {
      if (!resend) await save();
      const res = await sendContract(id);
      applyContract(res.data);
      toast.success('Signing links emailed to all signers');
    } catch (err) {
      toast.error(errMsg(err, 'Could not send contract'));
    } finally { setBusy(''); }
  };

  const handleRemind = async () => {
    setBusy('remind');
    try {
      const res = await remindContract(id);
      toast.success(`Reminder sent to ${res.data.reminded} signer${res.data.reminded === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error(errMsg(err, 'Could not send reminder'));
    } finally { setBusy(''); }
  };

  const handleDuplicate = async () => {
    setBusy('dup');
    try {
      const res = await duplicateContract(id);
      toast.success('Contract duplicated');
      navigate(`/admin/studio/contracts/${res.data.id}`);
    } catch (err) {
      toast.error(errMsg(err, 'Could not duplicate'));
      setBusy('');
    }
  };

  const handleCountersign = async () => {
    const signature = await promptDialog({
      title: 'Countersign contract',
      message: 'Type your full name to sign this contract. Everyone who signed gets the executed copy by email.',
      placeholder: 'Full name',
    });
    if (!signature) return;
    setBusy('sign');
    try {
      const res = await countersignContract(id, signature);
      applyContract(res.data);
      toast.success('Contract fully signed. The signed copy has been emailed to everyone.');
    } catch (err) {
      toast.error(errMsg(err, 'Could not sign'));
    } finally { setBusy(''); }
  };

  // The server builds the executed copy, so the studio's download is byte-for-byte
  // what the signers received.
  const handleDownload = async (format) => {
    setBusy('download');
    try {
      const res = await downloadContractDocument(id, format);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: format === 'pdf' ? 'application/pdf' : 'text/html' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${(form.name || 'Contract').replace(/[\\/:*?"<>|]+/g, '')} - signed.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errMsg(err, 'Could not download the signed copy'));
    } finally { setBusy(''); }
  };

  const handleCancel = async () => {
    const ok = await confirmDialog({ title: 'Cancel contract', message: 'Signers will no longer be able to sign. This cannot be undone.', danger: true, confirmText: 'Cancel contract' });
    if (!ok) return;
    setBusy('cancel');
    try {
      const res = await cancelContract(id);
      applyContract(res.data);
      toast.success('Contract canceled');
    } catch (err) {
      toast.error(errMsg(err, 'Could not cancel'));
    } finally { setBusy(''); }
  };

  const togglePreview = () => { if (!preview) setHtml(currentHtml()); setPreview(!preview); };

  const previewHtml = () => {
    const names = signers.map((s) => s.name).filter(Boolean).join(' and ');
    const vars = {
      client_name: signers[0]?.name || '', client_names: names, client_email: signers[0]?.email || '',
      project_name: form.project_name, event_date: fmtLong(form.event_date),
      studio_name: studioOf(form), today: fmtLong(new Date()),
    };
    for (const [k, f] of Object.entries(fields)) {
      if (!(k in vars)) vars[k] = f.type === 'date' ? fmtLong(f.value) : (f.value || '');
    }
    return html.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => (k in vars ? esc(vars[k]) : m));
  };

  const activity = () => {
    if (!contract) return [];
    const events = [{ t: contract.created_at, label: 'Contract created' }];
    if (contract.sent_at) events.push({ t: contract.sent_at, label: 'Sent to signers' });
    (contract.signers || []).forEach((s) => {
      if (s.viewed_at) events.push({ t: s.viewed_at, label: `${s.name} opened the contract` });
      if (s.signed_at) events.push({ t: s.signed_at, label: `${s.name} signed` });
    });
    if (contract.studio_signed_at) events.push({ t: contract.studio_signed_at, label: 'Studio countersigned' });
    if (contract.canceled_at) events.push({ t: contract.canceled_at, label: 'Contract canceled' });
    return events.filter((e) => e.t).sort((a, b) => new Date(a.t) - new Date(b.t));
  };

  if (authLoading || !user || user.role !== 'admin') return null;
  if (loading) return <div className="admin-page"><div className="admin-container"><p>Loading…</p></div></div>;
  if (loadError) {
    return (
      <div className="admin-page"><div className="admin-container">
        <button className="sc-back" onClick={() => navigate(backPath)}><ArrowLeft size={16} /> Contracts</button>
        <p>{loadError}</p>
      </div></div>
    );
  }

  const status = contract?.status;
  const roster = contract?.signers || [];

  return (
    <div className="admin-page">
      <div className="sc-editor-top">
        <button className="sc-back" onClick={() => navigate(backPath)}><ArrowLeft size={16} /> Contracts</button>
        <input className="sc-title-input" value={form.name} onChange={set('name')} disabled={readOnly} placeholder={isTemplate ? 'Template name' : 'Contract name'} />
        {status && <span className={`sc-badge sc-${status}`}>{CONTRACT_STATUS[status]}</span>}
        <div className="sc-top-actions">
          {!readOnly && (
            <>
              <button className="btn btn-outline btn-sm" onClick={togglePreview}>{preview ? 'Edit' : 'Preview'}</button>
              <button className="btn btn-outline btn-sm" onClick={handleSave} disabled={!!busy}>{busy === 'save' ? 'Saving…' : 'Save'}</button>
            </>
          )}
          {!isTemplate && status === 'draft' && (
            <button className="btn btn-primary btn-sm" onClick={handleSend} disabled={!!busy}><Send size={14} /> {busy === 'send' ? 'Sending…' : 'Send Contract'}</button>
          )}
          {status === 'awaiting_signature' && (
            <>
              <button className="btn btn-outline btn-sm" onClick={handleRemind} disabled={!!busy}><Bell size={14} /> {busy === 'remind' ? 'Sending…' : 'Remind'}</button>
              <button className="btn btn-outline btn-sm" onClick={handleSend} disabled={!!busy}>Resend</button>
            </>
          )}
          {status === 'countersign' && (
            <button className="btn btn-primary btn-sm" onClick={handleCountersign} disabled={!!busy}><Check size={14} /> Countersign</button>
          )}
          {!isTemplate && contract && (
            <button className="btn btn-outline btn-sm" onClick={handleDuplicate} disabled={!!busy} title="Duplicate"><Copy size={14} /></button>
          )}
          {!isTemplate && ['completed', 'countersign'].includes(status) && (
            <>
              <button className="btn btn-outline btn-sm" onClick={() => handleDownload('pdf')} disabled={!!busy} title="Download the signed copy as a PDF">
                <Download size={14} /> {busy === 'download' ? 'Preparing…' : 'PDF'}
              </button>
              <button className="btn btn-outline btn-sm" onClick={() => handleDownload('html')} disabled={!!busy} title="Download the signed copy as an HTML page">
                <Download size={14} /> HTML
              </button>
            </>
          )}
          {(status === 'completed' || readOnly) && (
            <button className="btn btn-outline btn-sm" onClick={() => window.print()} title="Print / Save PDF"><Printer size={14} /></button>
          )}
          {['awaiting_signature', 'countersign'].includes(status) && (
            <button className="btn btn-outline btn-sm sc-danger" onClick={handleCancel} disabled={!!busy}>Cancel</button>
          )}
        </div>
      </div>

      <div className="sc-editor-layout">
        <aside className="sc-side">
          {!isTemplate && (
            <>
              <h4>Signers</h4>
              {!readOnly ? (
                <>
                  {signers.map((s, i) => (
                    <div key={i} className="sc-signer-edit">
                      <div className="sc-signer-edit-head">
                        <span>{i === 0 ? 'Primary signer' : `Signer ${i + 1}`}</span>
                        {signers.length > 1 && <button type="button" className="sc-icon-btn" title="Remove" onClick={() => removeSigner(i)}><X size={14} /></button>}
                      </div>
                      <input placeholder="Full name" value={s.name} onChange={setSigner(i, 'name')} />
                      <input type="email" placeholder="email@example.com" value={s.email} onChange={setSigner(i, 'email')} />
                      <input type="tel" placeholder="Phone (optional, for SMS)" value={s.phone || ''} onChange={setSigner(i, 'phone')} />
                    </div>
                  ))}
                  {signers.length < 5 && (
                    <button type="button" className="sc-add-signer" onClick={addSigner}><Plus size={14} /> Add {signers.length === 1 ? 'second' : 'another'} signer</button>
                  )}
                </>
              ) : (
                <>
                  {roster.map((s) => (
                    <div key={s.id} className="sc-signer">
                      <span className="sc-avatar">{initial(s.name)}</span>
                      <span>
                        {s.name}
                        <small>{s.email}{s.phone ? ` · ${s.phone}` : ''}</small>
                        {s.signed_at ? <small className="sc-signed">Signed {fmtStamp(s.signed_at)}</small>
                          : s.viewed_at ? <small>Opened {fmtStamp(s.viewed_at)}</small>
                            : <small>Not yet opened</small>}
                      </span>
                    </div>
                  ))}
                  <div className="sc-signer">
                    <span className="sc-avatar">S</span>
                    <span>
                      Studio (you)
                      {contract?.studio_signed_at ? <small className="sc-signed">Signed {fmtStamp(contract.studio_signed_at)}</small> : <small>Signs after all clients</small>}
                    </span>
                  </div>
                </>
              )}

              <h4>
                Fields
                {!readOnly && emptyFields.length > 0 && <span className="sc-field-warn">{emptyFields.length} empty</span>}
              </h4>
              <p className="sc-meta sc-fields-hint">
                {readOnly ? 'Values used in this contract.' : 'Click a field to drop it into the document. Values are filled in when you send.'}
              </p>

              {!readOnly && (
                <div className="sc-chips">
                  {VARIABLES.map(([k, label]) => (
                    <button key={k} type="button" className="sc-chip" title={`Insert {{${k}}}`} onClick={() => insertField(k)}>
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {Object.keys(fields).sort().map((k) => {
                const f = fields[k] || { value: '', type: 'text' };
                const empty = !String(f.value || '').trim();
                return (
                  <div key={k} className={`sc-fieldrow ${empty && !readOnly ? 'empty' : ''}`}>
                    <div className="sc-fieldrow-head">
                      <button
                        type="button"
                        className="sc-fieldname"
                        title={`Insert {{${k}}} into the document`}
                        onClick={() => insertField(k)}
                        disabled={readOnly}
                      >
                        {fieldLabel(k)}
                      </button>
                      {!readOnly && (
                        <span className="sc-fieldrow-actions">
                          <select value={f.type} onChange={(e) => setFieldType(k, e.target.value)} title="Field type">
                            <option value="text">Text</option>
                            <option value="date">Date</option>
                            <option value="money">Amount</option>
                          </select>
                          <button type="button" className="sc-icon-btn" title="Remove field" onClick={() => removeField(k)}><X size={13} /></button>
                        </span>
                      )}
                    </div>
                    {f.type === 'date' ? (
                      <>
                        <input type="date" value={f.value || ''} onChange={(e) => setFieldValue(k, e.target.value)} disabled={readOnly} />
                        {f.value && <small className="sc-field-preview">{fmtLong(f.value)}</small>}
                      </>
                    ) : (
                      <input
                        type="text"
                        value={f.value || ''}
                        onChange={(e) => setFieldValue(k, e.target.value)}
                        disabled={readOnly}
                        placeholder={f.type === 'money' ? 'e.g. Rs. 150,000' : `Enter ${fieldLabel(k).toLowerCase()}`}
                      />
                    )}
                  </div>
                );
              })}
              {!readOnly && (
                <button type="button" className="sc-add-signer" onClick={addField}><Plus size={14} /> Add field</button>
              )}

              <h4>Email</h4>
              <label className="sc-field">
                <span>Company name the client sees</span>
                <input
                  value={form.studio_name}
                  onChange={set('studio_name')}
                  disabled={readOnly}
                  placeholder={DEFAULT_STUDIO}
                />
              </label>
              <p className="sc-meta sc-fields-hint">
                Used in the email, the SMS, the signing page and {'{{studio_name}}'}. Blank uses {DEFAULT_STUDIO}.
              </p>
              <label className="sc-field">
                <span>Email subject</span>
                <input
                  value={form.email_subject}
                  onChange={set('email_subject')}
                  disabled={readOnly}
                  placeholder={defaultSubject(form)}
                />
              </label>
              <p className="sc-meta sc-fields-hint">
                Leave blank for the default. You can use fields here too, such as {'{{client_name}}'}.
              </p>
              {!readOnly && (
                <div className="sc-subject-preview">
                  <small>Client sees</small>
                  <strong>{resolveText(form.email_subject.trim() || defaultSubject(form))}</strong>
                </div>
              )}

              <h4>Settings</h4>
              <label className="sc-setting">
                <span>
                  <strong><Clock size={13} /> Document expiry</strong>
                  <small>Auto-cancel if not completed by this date</small>
                </span>
                <input
                  type="checkbox"
                  checked={!!form.expires_at}
                  onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.checked ? defaultExpiry() : '' }))}
                  disabled={readOnly}
                />
              </label>
              {!!form.expires_at && (
                <label className="sc-field sc-reminder-interval">
                  <span>Expires on</span>
                  <input type="date" value={form.expires_at} onChange={set('expires_at')} disabled={readOnly} min={new Date().toISOString().slice(0, 10)} />
                </label>
              )}
              <label className="sc-setting">
                <span>
                  <strong><Bell size={13} /> Auto reminders</strong>
                  <small>Email unsigned signers on a schedule</small>
                </span>
                <input type="checkbox" checked={form.reminders_enabled} onChange={toggle('reminders_enabled')} disabled={readOnly} />
              </label>
              {form.reminders_enabled && (
                <label className="sc-field sc-reminder-interval">
                  <span>Remind every</span>
                  <select value={form.reminder_interval_days} onChange={set('reminder_interval_days')} disabled={readOnly}>
                    {[1, 2, 3, 5, 7, 14].map((d) => <option key={d} value={d}>{d} day{d === 1 ? '' : 's'}</option>)}
                  </select>
                </label>
              )}

              <h4>Details</h4>
              <label className="sc-field"><span>Project name</span><input value={form.project_name} onChange={set('project_name')} disabled={readOnly} /></label>
              <label className="sc-field"><span>Event date</span><input type="date" value={form.event_date} onChange={set('event_date')} disabled={readOnly} /></label>

              {contract && activity().length > 0 && (
                <>
                  <h4>Activity</h4>
                  <ul className="sc-timeline">
                    {activity().map((e, i) => (
                      <li key={i}><span className="sc-dot" /><div><strong>{e.label}</strong><small>{fmtStamp(e.t)}</small></div></li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
          {isTemplate && (
            <>
              <h4>Template</h4>
              <label className="sc-field"><span>Description</span><textarea rows={3} value={form.description} onChange={set('description')} /></label>
              <p className="sc-meta">Use variables such as {'{{client_names}}'} in the text. They are filled in when the contract is sent.</p>
            </>
          )}
        </aside>

        <main className="sc-main">
          {!readOnly && !preview && (
            <div className="sc-toolbar">
              <select className="sc-tb-select" defaultValue="" onChange={(e) => { exec('formatBlock', e.target.value || 'p'); e.target.value = ''; }}>
                <option value="">Normal</option>
                <option value="h2">Heading</option>
                <option value="h3">Subheading</option>
              </select>
              {toolbarBtn(<b>B</b>, 'Bold', () => exec('bold'))}
              {toolbarBtn(<i>I</i>, 'Italic', () => exec('italic'))}
              {toolbarBtn(<u>U</u>, 'Underline', () => exec('underline'))}
              {toolbarBtn(<s>S</s>, 'Strikethrough', () => exec('strikeThrough'))}
              <span className="sc-tb-sep" />
              {toolbarBtn('Left', 'Align left', () => exec('justifyLeft'))}
              {toolbarBtn('Center', 'Align center', () => exec('justifyCenter'))}
              {toolbarBtn('Right', 'Align right', () => exec('justifyRight'))}
              <span className="sc-tb-sep" />
              {toolbarBtn('• List', 'Bulleted list', () => exec('insertUnorderedList'))}
              {toolbarBtn('1. List', 'Numbered list', () => exec('insertOrderedList'))}
              {toolbarBtn('―', 'Divider', () => exec('insertHorizontalRule'))}
            </div>
          )}

          <div className="sc-paper">
            {readOnly || preview ? (
              <div className="sc-doc" dangerouslySetInnerHTML={{ __html: readOnly ? (contract.signed_content || contract.content) : previewHtml() }} />
            ) : (
              <div
                ref={editorRef}
                className="sc-doc sc-editable"
                contentEditable
                suppressContentEditableWarning
                onKeyUp={saveSelection}
                onMouseUp={saveSelection}
                onBlur={saveSelection}
              />
            )}

            {contract && (roster.some((s) => s.signature) || contract.studio_signature) && (
              <div className="sc-sigblock">
                {roster.map((s) => (
                  <div key={s.id}>
                    {s.signature_image
                      ? <div className="sc-sig sc-sig-drawn"><img src={s.signature_image} alt={`Signature of ${s.name}`} /></div>
                      : <span className="sc-sig">{s.signature || '—'}</span>}
                    <small>{s.name}{s.signed_at ? ` · ${fmtStamp(s.signed_at)}` : ' · awaiting signature'}</small>
                  </div>
                ))}
                <div>
                  <span className="sc-sig">{contract.studio_signature || 'Awaiting countersignature'}</span>
                  <small>Studio{contract.studio_signed_at ? ` · ${fmtStamp(contract.studio_signed_at)}` : ''}</small>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
      {ConfirmDialog}
      {PromptDialog}
    </div>
  );
}

export default ContractEditor;
